'use strict';

import moment from 'angular-moment';
import DateUtil from '../date_util/date_util.module';
import MathUtil from '../math_util/math_util';

class PowerFormatter {
  /*@ngInject*/
  constructor(moment, DateUtil, MathUtil) {
    this.moment = moment;
    this.DateUtil = DateUtil;
    this.MathUtil = MathUtil;
  }

  formatDemandPowers(demandPowers, targetDate, categories) {
    const missingTimes = [];

    if (demandPowers.length === 0) {
      return [[], []];
    }

    //先頭データの時刻と 00:30 を比較してデータの抜け数を計算
    let firstMeasuredAt = this.moment(demandPowers[0].measured_at);
    let beginningTime = this.moment(targetDate).startOf('day').add(30, 'minutes');
    let shift_range = firstMeasuredAt.diff(beginningTime, 'minutes') / 30;

    let formattedPowers = demandPowers.map((demandPower, index) => {
      if (demandPower.missing) {
        missingTimes.push(categories[index + shift_range]);
        return 0;
      }

      return demandPower.value;
    });

    //先頭のデータが存在しなかった場合、グラフ全体を後ろにずらす。
    for (let i = 0; i < shift_range; i++) {
      formattedPowers.unshift(null);
    }

    return [formattedPowers, missingTimes];
  }

  formatDailyDemandPowers(dailyMaxDemandPowers, categories) {
    let powersIndex = 0;

    return categories.map(category => {
      if (!dailyMaxDemandPowers[powersIndex]) {
        return {y: null};
      }

      const power = dailyMaxDemandPowers[powersIndex].value;
      const date = dailyMaxDemandPowers[powersIndex].date;

      let utc = this.DateUtil.jstToUTC(`${date}T00:00:00+09:00`);
      if (category === utc) {
        powersIndex++;
        return {y: power, date: this.moment(date).toDate()};
      } else {
        return {y: null};
      }
    });
  }

  formatMonthlyDemandPowers(monthlyMaxDemandPowers, categories, configBillingDate) {
    let powersIndex = 0;
    monthlyMaxDemandPowers = monthlyMaxDemandPowers.filter(power => {
      const date = this.moment(power.billing_date);
      return date.date() === configBillingDate;
    });

    return categories.map((category) => {
      if (!monthlyMaxDemandPowers[powersIndex]) {
        return {y: null};
      }

      const power = monthlyMaxDemandPowers[powersIndex].value;

      const date = monthlyMaxDemandPowers[powersIndex].billing_date;

      const billingDate = this.getStartOfMonth(date);
      const linkDate = this.moment(date).subtract(1, 'days').startOf('month');

      if (billingDate.isSame(category)) {
        powersIndex++;
        return {y: power, date: linkDate.toDate()};
      } else {
        return {y: null};
      }
    });
  }

  formatActivePowers(powers, categories, targetDate, power_upper_limit) {
    const missingTimes = [];
    const noCalculateTimes = [];

    if (powers.length === 0) {
      return [[], [], []];
    }

    //先頭データの時刻と 00:00 を比較してデータの抜け数を計算
    const firstMeasuredAt = this.moment(powers[0].measured_at);
    const beginningTime = this.moment(targetDate).startOf('day');
    const shift_range = firstMeasuredAt.diff(beginningTime, 'minutes') / 30;
    let formatted_powers = [];

    powers.forEach((power, index) => {
      if (!powers[index - 1]) {
        return;
      }
      // 例：10:00 のデータが欠損していた場合
      // 10:00 に「データなし」を表示する
      // 10:30 に「差分計算不可」を表示する
      if (power.missing) {
        missingTimes.push(categories[index + shift_range - 1]);
        formatted_powers.push(0);
      } else if (powers[index - 1].missing) {
        noCalculateTimes.push(categories[index + shift_range - 1]);
        formatted_powers.push(0);
      } else {
        const diff = this.calcDailyPowerDiff(powers[index].value, powers[index - 1].value, power_upper_limit);
        formatted_powers.push(diff);
      }
    });

    //先頭のデータが存在しなかった場合、グラフ全体を後ろにずらす。
    for (let i = 0; i < shift_range; i++) {
      formatted_powers.unshift(null);
    }

    return [formatted_powers, missingTimes, noCalculateTimes];
  }

  formatDailyActivePowers(dailyActivePowers, categories) {
    let powersIndex = 0;

    return categories.map(category => {
      if (!dailyActivePowers[powersIndex]) {
        return {y: null};
      }

      let power = dailyActivePowers[powersIndex].value;
      let date = dailyActivePowers[powersIndex].date;

      let utc = this.DateUtil.jstToUTC(`${date}T00:00:00+09:00`);
      if (category === utc) {
        powersIndex++;
        return {y: power, date: this.moment(date).toDate()};
      } else {
        return {y: null};
      }
    });
  }

  formatMonthlyActivePowers(monthlyActivePowers, categories, configBillingDate) {
    let powersIndex = 0;

    monthlyActivePowers = monthlyActivePowers.filter(power => {
      const date = this.moment(power.billing_date);
      return date.date() === configBillingDate;
    });

    return categories.map(category => {
      if (!monthlyActivePowers[powersIndex]) {
        return {y: null};
      }

      const power = monthlyActivePowers[powersIndex].value;
      const date = monthlyActivePowers[powersIndex].billing_date;

      const billingDate = this.getStartOfMonth(date);
      const linkDate = this.moment(date).subtract(1, 'days').startOf('month');

      if (billingDate.isSame(category)) {
        powersIndex++;
        return {y: power, date: linkDate.toDate()};
      } else {
        return { y: null };
      }
    });
  }

  calcDailyPowerDiff(current_value, before_value, power_upper_limit) {
    if (current_value < before_value) {
      const surplus = this.MathUtil.subtract(power_upper_limit, before_value);
      return this.MathUtil.add(surplus, current_value);
    }

    return this.MathUtil.subtract(current_value, before_value);
  }

  getStartOfMonth(date) {
    const tmpDate = this.moment(date).subtract(1, 'days').startOf('month');
    tmpDate.add(tmpDate.utcOffset(), 'minutes').utc();

    return tmpDate;
  }
}

export default angular.module('leptonApp.PowerFormatter', [moment, DateUtil, MathUtil])
  .service('PowerFormatter', ['moment', 'DateUtil', 'MathUtil', PowerFormatter])
  .name;



// WEBPACK FOOTER //
// ./public/dev/components/logic/power-formatter/power-formatter.logic.js